import { PenLine, CalendarCheck, CalendarDays, Type, SquareCheck } from 'lucide-react';
import { toPixels, toNormalised, maxLinesFor } from '../../utils/pdfOverlays';

/**
 * Shared vocabulary for SmartSign template fields — the SmartSign counterpart
 * of utils/pdfOverlays.js, reusing its geometry helpers
 * (normalised 0..1 coordinates, same rationale) while modelling SmartSign's
 * two field kinds:
 *
 *   - prefill: a board-column value stamped at generation time (blue)
 *   - signer:  a field the signer completes on the signing page
 *              (teal/purple by signer — DocuSign/GetSign convention)
 *
 * Three visual vocabularies by requirement (§4): blue = board data,
 * teal/purple = a human's input, per signer.
 */

export { toPixels, toNormalised, maxLinesFor };

/**
 * The size signer field values are stamped at — mirrors the fixed `fontSize: 10`
 * in the backend's buildCompletionStamp (utils/sign/signing-helpers.ts). The
 * "Fits N lines" hint and the signing page's text boxes both divide by this,
 * so a box the owner sized for three lines really holds three stamped lines.
 */
export const SIGNER_STAMP_FONT_SIZE = 10;

/**
 * The text a `date_signed` box is stamped with — mirrors the backend's
 * `formatSignedStamp` (utils/sign/signing-helpers.ts) exactly.
 *
 * The UTC day, not the browser's: near midnight the two are different dates,
 * and a preview that showed the signer's local day would name a day the
 * finished PDF does not.
 *
 * Mirrored rather than fetched because the signing page needs it BEFORE the
 * value exists — it fills the box the moment the signer signs, to show that the
 * app writes this one, not them.
 */
export function formatSignedStamp(date) {
  return date.toISOString().slice(0, 10);
}

export const KIND = {
  PREFILL: 'prefill',
  SIGNER: 'signer',
};

/**
 * Mirrors the server's MAX_SIGNERS (sign.constant.ts).
 *
 * Raised from 2 for the envelope-first design: roles merge by label when
 * templates are copied into an envelope, but two templates carrying genuinely
 * different parties reach three or four immediately. SIGNER_COLORS must hold at
 * least this many distinct entries.
 */
export const MAX_SIGNERS = 5;

/** Item-data overlays stay blue in both chip and placed form. */
export const PREFILL_STYLE = {
  color: '#2563EB',
  border: '1px solid rgba(37, 99, 235, 0.55)',
  background: 'rgba(37, 99, 235, 0.08)',
  chipBackground: 'rgba(37, 99, 235, 0.06)',
  chipBorder: '1px solid rgba(37, 99, 235, 0.35)',
};

/**
 * Signer palette, by 0-based signer index. Signer 1 = teal, signer 2 = purple
 * (§4 "standard DocuSign/GetSign convention"). Extend when MAX_SIGNERS grows.
 */
export const SIGNER_COLORS = [
  {
    name: 'teal',
    color: '#0D9488',
    border: '1px dashed rgba(13, 148, 136, 0.65)',
    background: 'rgba(13, 148, 136, 0.10)',
    chipBackground: 'rgba(13, 148, 136, 0.07)',
    chipBorder: '1px dashed rgba(13, 148, 136, 0.45)',
  },
  {
    name: 'purple',
    color: '#7C3AED',
    border: '1px dashed rgba(124, 58, 237, 0.65)',
    background: 'rgba(124, 58, 237, 0.10)',
    chipBackground: 'rgba(124, 58, 237, 0.07)',
    chipBorder: '1px dashed rgba(124, 58, 237, 0.45)',
  },
  // Signers 3–5. Chosen to stay distinct from the prefill blue (#2563EB) and
  // from each other in greyscale as well as in colour — a mis-read signer
  // colour means placing a signature box for the wrong party.
  {
    name: 'amber',
    color: '#B45309',
    border: '1px dashed rgba(180, 83, 9, 0.65)',
    background: 'rgba(180, 83, 9, 0.10)',
    chipBackground: 'rgba(180, 83, 9, 0.07)',
    chipBorder: '1px dashed rgba(180, 83, 9, 0.45)',
  },
  {
    name: 'pink',
    color: '#BE185D',
    border: '1px dashed rgba(190, 24, 93, 0.65)',
    background: 'rgba(190, 24, 93, 0.10)',
    chipBackground: 'rgba(190, 24, 93, 0.07)',
    chipBorder: '1px dashed rgba(190, 24, 93, 0.45)',
  },
  {
    name: 'green',
    color: '#15803D',
    border: '1px dashed rgba(21, 128, 61, 0.65)',
    background: 'rgba(21, 128, 61, 0.10)',
    chipBackground: 'rgba(21, 128, 61, 0.07)',
    chipBorder: '1px dashed rgba(21, 128, 61, 0.45)',
  },
];

export function signerColor(signerIndex) {
  return SIGNER_COLORS[signerIndex % SIGNER_COLORS.length];
}

/**
 * Per-type metadata for signer fields (§4, updated Aug 5): Signature,
 * Date signed (auto-stamped), Date (signer-picked), Text. Sizes are
 * normalised page fractions and mirror the server's constants
 * (sign.constant.ts) — the sanitiser clamps to the same minimums.
 */
export const SIGNER_FIELD_TYPES = {
  signature: {
    type: 'signature',
    label: 'Signature',
    verb: 'Sign',
    icon: PenLine,
    /**
     * Board column types this field's answer may be saved to.
     *
     * MIRRORS `SIGN_MAPPING_TARGETS` in the backend's sign.constant.ts — the
     * sanitiser there drops any pairing this list would not have offered, so a
     * mismatch shows up as a dropdown choice that silently fails to save.
     *
     * monday has no signature column: a signature is a PNG, and a File column
     * is where SmartForm has always put one.
     */
    mappingTargets: ['file'],
    defaultSize: { w: 0.2, h: 0.06 },
    minSize: { w: 0.1, h: 0.03 },
    /**
     * Signature boxes keep a sane aspect ratio (§4): height is derived from
     * width at resize time, so a pad-shaped box can't collapse into a line.
     */
    heightFromWidth: (w) => w * 0.3,
  },
  date_signed: {
    type: 'date_signed',
    label: 'Date signed (auto)',
    verb: 'Date signed',
    icon: CalendarCheck,
    mappingTargets: ['date'],
    /**
     * Same box as a Text field: 122×17pt on a 612×792 page. A date needs less
     * room than that to print, but a default that differs per type means two
     * fields dropped on the same line do not align, and the owner nudges
     * widths to match by hand. One default for every token field is the
     * cheaper promise to keep.
     */
    defaultSize: { w: 0.2, h: 0.022 },
    minSize: { w: 0.05, h: 0.015 },
  },
  date: {
    type: 'date',
    label: 'Date',
    verb: 'Date',
    icon: CalendarDays,
    mappingTargets: ['date'],
    /** Same 122×17pt box as Text and Date signed — see date_signed above. */
    defaultSize: { w: 0.2, h: 0.022 },
    minSize: { w: 0.05, h: 0.015 },
  },
  text: {
    type: 'text',
    label: 'Text field',
    verb: 'Text',
    icon: Type,
    /** Both spellings — monday's API says `long_text`, older field definitions
     *  say `long-text`, and ACCEPTED_COLUMN_TYPES carries both. */
    mappingTargets: ['text', 'long-text', 'long_text'],
    defaultSize: { w: 0.2, h: 0.022 },
    minSize: { w: 0.05, h: 0.015 },
  },
  checkbox: {
    type: 'checkbox',
    label: 'Checkbox',
    verb: 'Tick',
    icon: SquareCheck,
    /** monday's Checkbox column, as this codebase names it everywhere else. */
    mappingTargets: ['boolean'],
    /**
     * 17pt square, the height every other token field gets — a tick should read
     * as sitting on the same line as the date beside it.
     *
     * The two numbers differ because the two axes are normalised against
     * different lengths: `w` against page width, `h` against page height. On a
     * 612×792 page 0.0285×612 and 0.022×792 are both ~17.4pt. Writing 0.022
     * twice, which is what this used to say, is 17.4pt tall and 13.5pt wide —
     * the mistake this comment exists to stop being made again.
     *
     * Only `w` is live: `square` derives the height from it at drop and at
     * resize. `h` is kept at the token fields' fraction so that a reader — or a
     * consumer that forgets to check `square` — still lands on 17pt.
     */
    defaultSize: { w: 0.0285, h: 0.022 },
    minSize: { w: 0.015, h: 0.015 },
    /**
     * Kept square, the way a signature keeps its aspect ratio — height follows
     * width. A stretched checkbox reads as an empty text box, and the stamped
     * mark is centred in whatever it is given, so a wide one puts the tick
     * nowhere near the printed box it is meant to fill.
     *
     * Pages are taller than they are wide, so equal FRACTIONS are not equal
     * lengths: `snapSignerSize` converts using the page aspect at resize time,
     * where it is known.
     */
    square: true,
  },
};

export const SIGNER_FIELD_TYPE_LIST = Object.values(SIGNER_FIELD_TYPES);

/**
 * Size a newly dropped ITEM-DATA field gets, normalised. Exported because the
 * drag ghost has to be the same size as the drop, and two copies of a magic
 * number is how they stop being.
 */
export const PREFILL_DEFAULT_SIZE = { w: 0.32, h: 0.022 };

// ── required-marker geometry ────────────────────────────────────────────────
//
// A placement can be 120x51 or 13x13, so "show that it is required" has to be
// a decision about space rather than one style. Measured in the app's own font
// at 8px: "required" is 34px wide, and a box that shows it keeps
//   width - 8 (padding) - 9 (icon) - 8 (gaps) - 34 - 4 = width - 63
// for the label. Below ~100px that leaves nothing usable, so the word is
// dropped and the corner mark carries the meaning alone.
//
// Exported and tested because these four numbers ARE the design; inline in the
// render they would drift the first time a box size changed.

/** Below this in either axis the corner triangle would swallow the box. */
export const REQUIRED_MARK_MIN = 16;
export const REQUIRED_WORD_MIN_W = 100;
export const REQUIRED_WORD_MIN_H = 16;
/** Signature boxes stack icon / label / word, so the word needs its own line. */
export const REQUIRED_WORD_MIN_H_STACKED = 40;

/** 'corner' (6px triangle) or 'dot' (4px) — a checkbox only has room for a dot. */
export function requiredMarkKind(px) {
  return px.width < REQUIRED_MARK_MIN || px.height < REQUIRED_MARK_MIN ? 'dot' : 'corner';
}

/**
 * Can this box hold the word without erasing its label?
 *
 * When false the caller shows the word on hover/selection instead — which is
 * how a 13px checkbox stays learnable without a tag sitting over the PDF for
 * the 99% of the time nobody is pointing at it.
 */
export function requiredWordFits(px, stacked = false) {
  const minHeight = stacked ? REQUIRED_WORD_MIN_H_STACKED : REQUIRED_WORD_MIN_H;
  return px.width >= REQUIRED_WORD_MIN_W && px.height >= minHeight;
}

/**
 * Resize snap grid (§4 "resizable at a defined scale — fixed steps"), as a
 * fraction of the page. 1% steps are fine enough to feel free and coarse
 * enough that two boxes resized to "the same size" actually match.
 */
export const SNAP_STEP = 0.01;

const snap = (value) => Math.round(value / SNAP_STEP) * SNAP_STEP;

/**
 * Quantise a signer field's size to the snap grid, clamp to the per-type
 * minimum, and enforce the signature aspect ratio. Prefill fields snap too
 * but keep SmartForm's free aspect.
 */
export function snapSignerSize(type, w, h, pageAspect = 1) {
  const meta = SIGNER_FIELD_TYPES[type] || SIGNER_FIELD_TYPES.text;
  let outW = Math.max(snap(w), meta.minSize.w);
  let outH = Math.max(snap(h), meta.minSize.h);
  if (meta.heightFromWidth) outH = Math.max(meta.heightFromWidth(outW), meta.minSize.h);
  // `pageAspect` is width/height in page units. A square on the PAGE needs
  // h = w * aspect in normalised fractions, since the two axes are scaled by
  // different amounts. Defaults to 1 so a caller with no page in hand still
  // gets a sane box rather than a wrong one.
  if (meta.square) outH = Math.max(outW * pageAspect, meta.minSize.h);
  return { w: Math.min(outW, 1), h: Math.min(outH, 1) };
}

export function snapPrefillSize(w, h) {
  return {
    w: Math.min(Math.max(snap(w), 0.01), 1),
    h: Math.min(Math.max(snap(h), 0.005), 1),
  };
}

/**
 * Patch resizing a signer field to a typed PDF-point size, top-left fixed —
 * the signer-field sibling of pdfOverlays' `resizeOverlayPt`. No snap grid:
 * snapping belongs to drag gestures; a typed "45" must store 45, not 44.
 *
 * Per-type minimums match the server's clamp-up, and signature boxes keep
 * their enforced aspect: width drives height, and a typed height is ignored
 * (the panel disables that input rather than silently fighting it).
 */
export function resizeSignerFieldPt(field, pagePt, next) {
  const meta = SIGNER_FIELD_TYPES[field.type] || SIGNER_FIELD_TYPES.text;
  const patch = {};
  if (Number.isFinite(next.w)) {
    patch.w = Math.min(Math.max(next.w / pagePt.width, meta.minSize.w), 1 - field.x);
  }
  if (Number.isFinite(next.h) && !meta.heightFromWidth && !meta.square) {
    patch.h = Math.min(Math.max(next.h / pagePt.height, meta.minSize.h), 1 - field.y);
  }
  if (meta.heightFromWidth && patch.w !== undefined) {
    patch.h = Math.min(Math.max(meta.heightFromWidth(patch.w), meta.minSize.h), 1 - field.y);
  }
  // A typed width sets the height to match in POINTS, so the box is square on
  // the page rather than square in normalised fractions.
  if (meta.square && patch.w !== undefined) {
    const points = patch.w * pagePt.width;
    patch.h = Math.min(Math.max(points / pagePt.height, meta.minSize.h), 1 - field.y);
  }
  return patch;
}

/** Overlay label (§4): item data `{Amount}`; signer `Sign — Client`. */
export function overlayLabel(field, columnTitles, signers) {
  if (field.kind === KIND.PREFILL) {
    return `{${columnTitles.get(field.columnId) || field.columnId}}`;
  }
  const meta = SIGNER_FIELD_TYPES[field.type] || SIGNER_FIELD_TYPES.text;
  const who = signers?.[field.signerIndex]?.roleLabel || `Signer ${field.signerIndex + 1}`;
  return `${field.label || meta.verb} — ${who}`;
}

/** Merge the two stored arrays into the single list the canvas renders. */
export function mergeFields(prefillFields, signerFields) {
  return [
    ...(prefillFields || []).map((f) => ({ ...f, kind: KIND.PREFILL })),
    ...(signerFields || []).map((f) => ({ ...f, kind: KIND.SIGNER })),
  ];
}

/** Split the canvas's merged list back into the two stored arrays. */
export function splitFields(fields) {
  const prefillFields = [];
  const signerFields = [];
  for (const f of fields) {
    const { kind, ...rest } = f;
    if (kind === KIND.PREFILL) prefillFields.push(rest);
    else signerFields.push(rest);
  }
  return { prefillFields, signerFields };
}

/** Stable-enough id for a new field; the server re-checks and may replace it. */
export function newFieldId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}
